import { AlertTriangle, Bot, Brain, FileText, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { formatDateTime } from "@/utils/formatDate";
import { useAronApiBase } from "../api/aronApi";
import { AronWordmark } from "./AronMark";
import { useAronYolo } from "./aronYolo";
import { AronMarkdown } from "./AronMarkdown";
import { AronToolStep } from "./AronToolStep";
import { AronSources } from "./AronSources";
import { AronFileResult } from "./AronFileResult";
import { AronImageResult } from "./AronImageResult";
import { AronWriteProposal } from "./AronWriteProposal";
import { AronBatchAccept } from "./AronBatchAccept";
import { AronBudgetNotice } from "./AronBudgetNotice";
import { AronQualityGate } from "./AronQualityGate";
import type { AronMessage, AronFileResult as FileT } from "../types";

/** GG:MM:SS wiadomości. Rozmowa z ARON-em to sekwencja zleceń i powrotów po
 *  zadaniach w tle — bez sekund nie widać, czy między „zleciłem" a „wróciłem" minęła
 *  minuta czy kwadrans (zgłoszenie właściciela 04.09.2026: eksport pokazywał tylko
 *  14:36 / 14:47, a na ekranie nie było nic). */
function fmtTime(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

const isImage = (f: FileT) =>
  (f.content_type || "").startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|svg)$/i.test(f.filename || "");

/* Reasoning streams in token by token; the box follows the newest line, the way
   a log tail does — unless the reader scrolled up to re-read something. */
/** Usuwa doklejoną stopkę kontrolera z treści — w widoku pokazuje ją osobny blok. */
function stripGateNote(text: string): string {
  const i = text.indexOf("🛡 **KONTROLER JAKOŚCI");
  return i === -1 ? text : text.slice(0, i).trimEnd();
}

function AronThinking({ text, open }: { text: string; open: boolean }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  useEffect(() => {
    const el = boxRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [text]);

  // A closed <details> has no layout, so scrollTop set while hidden is lost —
  // land at the bottom again when it is reopened.
  const toBottom = () => {
    const el = boxRef.current;
    if (!el) return;
    stick.current = true;
    el.scrollTop = el.scrollHeight;
  };

  return (
    <details
      open={open}
      onToggle={(e) => e.currentTarget.open && toBottom()}
      className="mb-2 border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50"
    >
      <summary className="flex cursor-pointer items-center gap-1.5 px-2 py-1 text-xs text-gray-500 select-none">
        <Brain size={12} />
        {open ? "Myślę…" : "Rozumowanie"}
        {open && <Loader2 size={11} className="animate-spin" />}
      </summary>
      <div
        ref={boxRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
        className="max-h-60 overflow-y-auto whitespace-pre-wrap break-words px-2 pb-2 text-xs leading-relaxed text-gray-500"
      >
        {text}
      </div>
    </details>
  );
}

export function AronMessageBubble({
  message,
  conversationId,
  readOnly = false,
}: {
  message: AronMessage;
  conversationId: string | null;
  /** Tryb podglądu cudzej rozmowy — bez przycisków decyzji. */
  readOnly?: boolean;
}) {
  // Czerwień jest kolorem zwykłej rozmowy; w YOLO cała oprawa idzie na czarno.
  const yolo = useAronYolo();
  const isAron = useAronApiBase() === "/aron";

  if (message.role === "user") {
    const atts = message.attachments ?? [];
    return (
      <div className="flex justify-end">
        <div className="flex min-w-0 max-w-[80%] flex-col items-end gap-1.5">
          {/* Dopisek stoi w numeracji ZA odpowiedzią, na którą wpłynął — bez podpisu
              czytelnik widziałby „odpowiedź, a potem nowe pytanie bez odpowiedzi". */}
          {message.addendum_to && (
            <div
              className="text-[11px] text-gray-400"
              title={
                message.addendum_seen_at
                  ? "Wysłane w trakcie odpowiedzi powyżej — ARON uwzględnił to w jej dalszej części."
                  : "Wysłane w trakcie odpowiedzi powyżej — dotarło po jej zakończeniu, odpowiedź osobno poniżej."
              }
            >
              {message.addendum_seen_at
                ? "dopisano w trakcie odpowiedzi · uwzględnione"
                : "dopisano w trakcie odpowiedzi"}
            </div>
          )}
          {atts.length > 0 && (
            <div className="flex flex-wrap justify-end gap-1.5">
              {atts.map((a) =>
                (a.content_type || "").startsWith("image/") ? (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                    <img
                      src={a.url}
                      alt={a.filename}
                      className="max-h-40 max-w-[12rem] rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                    />
                  </a>
                ) : (
                  <a
                    key={a.id}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    title={a.filename}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                  >
                    <FileText size={14} />
                    <span className="max-w-[10rem] truncate">{a.filename}</span>
                  </a>
                ),
              )}
            </div>
          )}
          {fmtTime(message.created_at) && (
            <div className="text-[11px] tabular-nums text-gray-400" title={formatDateTime(message.created_at)}>
              {fmtTime(message.created_at)}
            </div>
          )}
          {message.text && (
            <div
              className={`[overflow-wrap:anywhere] whitespace-pre-wrap border border-r-[3px] px-3 py-2 text-sm leading-snug text-gray-900 dark:text-gray-100 ${
                yolo
                  ? "border-gray-300 border-r-black bg-gray-100 dark:border-gray-600 dark:border-r-white dark:bg-gray-800"
                  : "border-red-200 border-r-red-600 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20"
              }`}
            >
              {message.text}
            </div>
          )}
          {/* Wysyłka odrzucona przez serwer — tekst zostaje (do skopiowania albo
              poprawienia), ale wątek nie udaje, że wiadomość poszła. */}
          {message.status === "error" && (
            <div className="flex max-w-full items-start gap-1.5 border border-red-300 bg-red-50 px-2.5 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                <strong className="font-medium">Nie wysłano.</strong>{" "}
                {message.error || "Serwer odrzucił wiadomość."}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const streaming = message.status === "streaming" || message.status === "pending";
  const hasThinking = !!message.thinking;

  return (
    <div className="flex gap-2">
      <span
        className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center text-white ${
          yolo ? "bg-black dark:bg-white dark:text-black" : "bg-red-600"
        }`}
      >
        {isAron ? <AronWordmark /> : <Bot size={15} />}
      </span>
      <div className="min-w-0 flex-1">
        {fmtTime(message.created_at) && (
          <div className="mb-1 text-[11px] tabular-nums text-gray-400" title={formatDateTime(message.created_at)}>
            {fmtTime(message.created_at)}
          </div>
        )}
        {message.tool_steps.length > 0 && (
          <div className="mb-2 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-700">
            {message.tool_steps.map((s) => (
              <AronToolStep key={s.id} step={s} />
            ))}
          </div>
        )}

        {/* Live reasoning — open while streaming so you can judge if it's on track. */}
        {hasThinking && (
          <AronThinking
            text={message.thinking ?? ""}
            open={streaming && !message.text}
          />
        )}

        {message.status === "error" ? (
          <div className="flex items-start gap-1.5 border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{message.error || "Wystąpił błąd."}</span>
          </div>
        ) : message.text ? (
          <AronMarkdown
            /* Ocena kontrolera ma własny blok poniżej — w treści zostaje dla druku
               i eksportu, ale w widoku nie dublujemy jej jako akapitu. */
            text={message.gate ? stripGateNote(message.text) : message.text}
            conversationId={conversationId}
          />
        ) : streaming && !hasThinking ? (
          <div className="flex items-center gap-1.5 text-sm text-gray-400">
            <Loader2 size={13} className="animate-spin" /> ARON pisze…
          </div>
        ) : null}

        {message.gate && (
          <AronQualityGate gate={message.gate} conversationId={conversationId} />
        )}
        {message.status === "cancelled" && (
          <div className="mt-1 text-xs text-gray-400">(przerwano)</div>
        )}
        {(message.stop_reason === "budget" ||
          message.stop_reason === "daily_budget") && (
          <AronBudgetNotice
            conversationId={conversationId}
            reason={message.stop_reason}
          />
        )}
        {message.files && message.files.some((f) => isImage(f)) && (
          <div className="mt-2 flex flex-col gap-2">
            {message.files.filter((f) => isImage(f)).map((f) => (
              <AronImageResult key={f.id} file={f} />
            ))}
          </div>
        )}
        {message.files && message.files.some((f) => !isImage(f)) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {message.files.filter((f) => !isImage(f)).map((f) => (
              <AronFileResult key={f.id} file={f} />
            ))}
          </div>
        )}
        {conversationId && message.pending_writes && message.pending_writes.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {!readOnly && (
              <AronBatchAccept
                conversationId={conversationId}
                writes={message.pending_writes}
              />
            )}
            {message.pending_writes.map((w) => (
              <AronWriteProposal
                key={w.id}
                conversationId={conversationId}
                write={w}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
        <AronSources sources={message.sources} conversationId={conversationId} />
        {message.status === "streaming" && (message.provider_calls ?? 0) > 0 && (
          <div
            className="mt-1 text-[11px] text-gray-400"
            title="Koszt tej odpowiedzi DOTĄD — rośnie z każdym zapytaniem do dostawcy, także za pliki czytane po kolei (analyze_files). Stop przerywa między zapytaniami."
          >
            koszt dotąd: $
            {Number(message.cost_usd ?? 0) >= 0.01
              ? Number(message.cost_usd ?? 0).toFixed(2)
              : Number(message.cost_usd ?? 0).toFixed(4)}{" "}
            · {message.provider_calls}{" "}
            {message.provider_calls === 1
              ? "zapytanie"
              : (message.provider_calls ?? 0) < 5
                ? "zapytania"
                : "zapytań"}{" "}
            do dostawcy
          </div>
        )}
        {message.status === "done" && message.cost_usd && Number(message.cost_usd) > 0 && (
          <div
            className="mt-1 text-[11px] text-gray-400"
            title={
              // Model TEJ tury, nie rozmowy — po przełączeniu modelu w trakcie wątku
              // to jedyne miejsce, gdzie widać, komu zapłacono za tę odpowiedź.
              `Koszt tej odpowiedzi (tokeny)${
                message.model_id
                  ? ` · model: ${message.model_label || message.model_id}`
                  : ""
              }`
            }
          >
            koszt: ${Number(message.cost_usd) >= 0.01
              ? Number(message.cost_usd).toFixed(2)
              : Number(message.cost_usd).toFixed(4)}
          </div>
        )}
      </div>
    </div>
  );
}
